import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Copy, RefreshCw, Eye, EyeOff, KeyRound } from "lucide-react";
import { passwordSchema } from "@/lib/passwordValidation";

interface AdminSetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  userEmail: string | null;
  userName?: string | null;
}

function generatePassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const nums = "23456789";
  const special = "!@#$%^&*";
  const all = upper + lower + nums + special;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  const chars = [pick(upper), pick(lower), pick(nums), pick(special)];
  for (let i = 0; i < 10; i++) chars.push(pick(all));
  // shuffle
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

export function AdminSetPasswordDialog({
  open,
  onOpenChange,
  userId,
  userEmail,
  userName,
}: AdminSetPasswordDialogProps) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successPassword, setSuccessPassword] = useState<string | null>(null);
  const [authEmail, setAuthEmail] = useState<string | null>(null);

  const reset = () => {
    setPassword("");
    setError(null);
    setSuccessPassword(null);
    setAuthEmail(null);
    setSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleGenerate = () => {
    setPassword(generatePassword());
    setError(null);
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleSubmit = async () => {
    if (!userId) return;
    setError(null);

    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.errors[0]?.message ?? "Invalid password");
      return;
    }

    setSubmitting(true);
    const { data, error: fnError } = await supabase.functions.invoke("admin-set-password", {
      body: { user_id: userId, new_password: password },
    });
    setSubmitting(false);

    if (fnError || (data && data.error)) {
      const msg = (data && data.error) || fnError?.message || "Failed to set password";
      setError(msg);
      toast.error(msg);
      return;
    }

    toast.success("Password updated");
    if (data?.user_email && data.user_email !== userEmail) {
      setAuthEmail(data.user_email);
    }
    setSuccessPassword(password);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" /> Set Password
          </DialogTitle>
          <DialogDescription>
            Manually set a password for{" "}
            <span className="font-medium">{userName || userEmail || "this user"}</span>. Use
            it to sign in and verify access before sharing credentials.
          </DialogDescription>
        </DialogHeader>

        {successPassword ? (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="text-muted-foreground mb-1">Sign-in email</div>
              <div className="flex items-center gap-2 mb-2">
                <code className="font-mono break-all flex-1">{authEmail || userEmail}</code>
                <Button size="icon" variant="ghost" onClick={() => handleCopy(authEmail || userEmail || "")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-muted-foreground mb-1">Password</div>
              <div className="flex items-center gap-2">
                <code className="font-mono break-all flex-1">{successPassword}</code>
                <Button size="icon" variant="ghost" onClick={() => handleCopy(successPassword)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {authEmail && authEmail !== userEmail && (
              <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
                Heads up: this user's sign-in email (<span className="font-mono">{authEmail}</span>) is different from the email on their profile (<span className="font-mono">{userEmail}</span>). Sign in with the sign-in email shown above.
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Save this password now — it will not be shown again after you close this dialog.
            </p>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="new-password"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter or generate a password"
                    className="pr-9 font-mono"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button type="button" variant="outline" onClick={handleGenerate} title="Generate">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Min 8 chars with upper, lower, number, and special character.
              </p>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || !password}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Set Password
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
